import {SsnForm} from '@/APIs/types';
import {CaseManagerStore} from '@/CaseManager/CaseManagerStore';
import useExitWarning from '@/Core/hooks/useExitWarning';
import RootStore from '@/Core/mobx/RootStore';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react-lite';
import {FC, useState} from 'react';
import {generatePath, useHistory, useLocation, useParams, useRouteMatch} from 'react-router-dom';
import {AgentStore} from '../AgentStore';
import SelectContact from '../PreQualification/SelectContact';
import {IS_PRODUCTION} from '../../constants';
import {Tooltip} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {makeStyles} from '@material-ui/core/styles';

interface ContactFormProps {
    store: AgentStore | CaseManagerStore;
}

interface MatchParams {
    tabIndex: string;
}

const FORM_TYPE_MAPPING: Record<string, 'prequal' | 'quote' | 'preapproval'> = {
    '0': 'prequal',
    '1': 'quote',
    '2': 'preapproval',
};

let resolveConfirmSave: (confirm: boolean) => void;

const useStyles = makeStyles({
    formControl: {
        marginTop: 8,
        width: '100%',
    },
    tooltip: {
        maxWidth: 1000,
        width: '100%',
        border: '1px solid gray',
        fontSize: 12,
    },
});

export const ContactForm: FC<ContactFormProps> = observer(({store}) => {
    const {historyFormId} = useParams<any>();
    const {contactsFormStore, parameterStore} = store;
    const [loading, setLoading] = useState(false);
    const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
    const [duplicateSsnForms, setDuplicateSsnForms] = useState<SsnForm[]>([]);
    const warningOffset = !RootStore.currentAgentStates.includes(parameterStore.approval.state.value) ? 80 : 0;

    const classes = useStyles();

    useExitWarning(parameterStore.formDirty.size > 0);

    const history = useHistory();
    const {search} = useLocation();
    const match = useRouteMatch<MatchParams>();

    const {familyFormId, familyEmail} = Object.fromEntries(new URLSearchParams(search));

    const formType = FORM_TYPE_MAPPING[match.params.tabIndex];

    const confirmSave = async (): Promise<boolean> => {
        const response = await new Promise<boolean>((resolve) => {
            resolveConfirmSave = resolve;
            setSaveConfirmationOpen(true);
        });

        setSaveConfirmationOpen(false);

        return response;
    };

    const confirmPendingSave = () => {
        resolveConfirmSave(true);
    };

    const cancelPendingSave = () => {
        resolveConfirmSave(false);
    };

    const saveForm = async () => {
        setLoading(true);

        const formsWithSameSsn =
            await RootStore.agentPortalStore.contactsFormStore.getSsnFormsAssociatedWithCurrentFormSsn();
        const duplicateSsnForms = formsWithSameSsn.filter((form) => form.formId !== parameterStore.formid);

        if (duplicateSsnForms.length > 0) {
            setDuplicateSsnForms(duplicateSsnForms);
            if (!(await confirmSave())) {
                setLoading(false);
                return;
            }
        }

        const form = await contactsFormStore.saveForm(formType);
        const newPath = generatePath('/agent/form/:email/:formId/:tabIndex', {
            email: form?.contact,
            formId: form?.formid,
            tabIndex: match.params.tabIndex,
        });
        window.history.replaceState(null, '', newPath);
        history.push(newPath);
        RootStore.uiStore.showSnackbar('Form successfully saved', 'success');
        setLoading(false);
    };

    return (
        <Box mx="30px" position="sticky" top={150 + warningOffset} zIndex={10}>
            <Card style={{padding: 20, zIndex: 9, position: 'relative'}}>
                <Grid container spacing={3}>
                    <Grid item xs={3}>
                        <TextField
                            variant="outlined"
                            label="Case Name"
                            value={contactsFormStore.name}
                            onChange={(e) => {
                                runInAction(() => {
                                    contactsFormStore.name = e.target.value;
                                });
                            }}
                            style={{width: '100%'}}
                        />
                    </Grid>
                    <Grid item xs>
                        <SelectContact
                            contactSelected={(email) => {
                                runInAction(() => {
                                    contactsFormStore.contact = email;
                                    parameterStore.contact = email;
                                });
                            }}
                        ></SelectContact>
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            variant="outlined"
                            label="Contact"
                            disabled
                            value={contactsFormStore.contact}
                            onChange={(e) => {
                                runInAction(() => {
                                    contactsFormStore.contact = e.target.value;
                                });
                            }}
                            style={{width: '100%'}}
                        />
                    </Grid>

                    <Grid item xs>
                        <Button
                            disabled={
                                loading || !contactsFormStore.contact || historyFormId || familyEmail || familyFormId
                            }
                            variant="outlined"
                            color="primary"
                            style={{marginTop: 12, width: '100%'}}
                            onClick={async () => {
                                setLoading(true);
                                const form = await contactsFormStore.saveForm(formType);
                                await contactsFormStore.startFamilyForm({
                                    id: form?.formid,
                                    email: form?.contact,
                                });
                                window.open(
                                    `/agent/newform/2?familyFormId=${form?.formid}&familyEmail=${form?.contact}`,
                                    '_blank',
                                );
                                RootStore.uiStore.showSnackbar('Form successfully saved', 'success');
                                setLoading(false);
                            }}
                        >
                            + Family Policy
                        </Button>
                    </Grid>

                    <Grid item xs>
                        <Button
                            disabled={loading || !contactsFormStore.contact || historyFormId}
                            variant="outlined"
                            color="primary"
                            style={{marginTop: 12, width: '100%'}}
                            onClick={saveForm}
                        >
                            Save
                        </Button>
                    </Grid>

                    {historyFormId && (
                        <Grid item xs>
                            <Button
                                disabled={loading}
                                variant="outlined"
                                color="primary"
                                style={{marginTop: 12, width: '100%'}}
                                onClick={async () => {
                                    parameterStore.formid = '';
                                    saveForm();
                                }}
                            >
                                Save as a form
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </Card>
            <Dialog
                open={saveConfirmationOpen}
                onClose={() => cancelPendingSave()}
                aria-labelledby="confirm-dialog-title"
            >
                <DialogTitle id="confirm-dialog-title">Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>The SSN is associated with following forms:</DialogContentText>
                    <DialogContentText>
                        <ul>
                            {duplicateSsnForms.map(({formId, email}) => (
                                <li>
                                    {formId} - {email}
                                </li>
                            ))}
                        </ul>
                    </DialogContentText>
                    <DialogContentText>Please double check before confirming save.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => cancelPendingSave()} color="primary">
                        Cancel
                    </Button>
                    <Button color="primary" onClick={() => confirmPendingSave()}>
                        Confirm Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
});
